import find from 'lodash-es/find';
import isEmpty from 'lodash-es/isEmpty';
import isNil from 'lodash-es/isNil';
import maxBy from 'lodash-es/maxBy';
import minBy from 'lodash-es/minBy';
import moment from 'moment-timezone';
import { useCallback, useMemo } from 'react';
import { useClient, useItemContext, useQuery } from '../..';
import { isForecast, isMetric } from '../../enums/MetricIds';
import { forecastsToMetricTuple, getMetricToFetch } from '../forecasts';
import { computeUntilMidnight } from '../measurements';
import { getAggregateValue, getAggregationLabel, isAggregationId, isBackEndAggregationId } from '../summary';
import useMetricProvider from './useMetricProvider';
const DEW_POINT_THRESHOLD = 0.5;
/**
 * this method allow requesting every forecast in one rest call
 * @returns forecast value for every metrics passed by expect ept
 */
export const useMetricSummaryForecastsData = ({ forecastsPeriod, metrics }) => {
    const client = useClient();
    const { kind, id } = useItemContext();
    const isPlot = kind === 'plot';
    const forecastMetricsToFetch = metrics.reduce((acc, metric) => {
        const toFetch = getMetricToFetch(metric).filter(isForecast);
        acc = acc.concat(toFetch);
        return acc;
    }, []);
    const forecastsResults = useQuery(client.forecasts.getForecasts({
        plotId: isPlot ? id : undefined,
        deviceId: !isPlot ? id : undefined,
        ...forecastsPeriod,
        //don't include etp because we only want etp from summary
        metrics: forecastMetricsToFetch
    }), { enabled: forecastsPeriod && !isEmpty(forecastMetricsToFetch) }).data;
    return forecastsResults;
};
const useMetricSummary = ({ forecastsResults, metric, summary, resource, isExpertOrg }) => {
    // WARNING we may display a metric summary for a metric that is not in available measures.
    // In that case we want to use forecasts values.
    const { availableMeasures: availableMeasuresFromCtx, timezone } = useItemContext();
    const availableMeasures = availableMeasuresFromCtx || [];
    const isMetricInAvailableMeasures = availableMeasures.includes(metric);
    const { name: metricProvider, isVirtualDevice, warningThreshold, deviceId } = useMetricProvider({ resource, metric, isExpertOrg });
    const forecasts = isForecast(metric) ? forecastsToMetricTuple(metric, forecastsResults) : [];
    // A dew point risk exists if | T_DEW_L - T_L | < 0,5
    const hasDewRisk = useMemo(() => {
        if (!isNil(summary.T_DEW_L) && !isNil(summary.T_L)) {
            return Math.abs(summary.T_DEW_L - summary.T_L) < DEW_POINT_THRESHOLD;
        }
        else {
            return false;
        }
    }, [summary.T_DEW_L, summary.T_L]);
    const getFullSummaryValue = useCallback((aggregationIdOrMetric, backEndAggregationsId) => {
        const label = getAggregationLabel(aggregationIdOrMetric);
        const aggregationLabel = backEndAggregationsId
            ? getAggregationLabel(backEndAggregationsId)
            : undefined;
        let fullSummaryValue = {
            label,
            aggregationId: aggregationIdOrMetric,
            aggregationLabel
        };
        // if the wanted value is a different aggregation of the same metric
        if (isAggregationId(aggregationIdOrMetric)) {
            let value = null;
            let timestamp;
            // if we can get the value directly from the fetched summary
            if (isBackEndAggregationId(aggregationIdOrMetric)) {
                const result = getAggregateValue(metric, aggregationIdOrMetric, summary);
                value = result.value;
                timestamp = result.timestamp;
            }
            let fromForecasts = false;
            // if we want min or max of day, we need to take a look at forecasts to
            if (aggregationIdOrMetric === 'minToday') {
                const forecastsMin = minBy(forecasts, (datum) => datum[1]);
                if (forecastsMin &&
                    forecastsMin[1] !== null &&
                    ((value != null && forecastsMin[1] < value) || value === null)) {
                    value = forecastsMin[1];
                    timestamp = forecastsMin[0];
                    fromForecasts = true;
                }
            }
            if (aggregationIdOrMetric === 'maxToday') {
                const forecastsMax = maxBy(forecasts, (datum) => datum[1]);
                if (forecastsMax &&
                    forecastsMax[1] !== null &&
                    ((value != null && forecastsMax[1] > value) || value === null)) {
                    value = forecastsMax[1];
                    timestamp = forecastsMax[0];
                    fromForecasts = true;
                }
            }
            // if we want sum for last hour but metric is not in available measures
            if (aggregationIdOrMetric === 'lastSlidingHour' && !isMetricInAvailableMeasures) {
                const datum = find(forecasts, (d) => parseInt(d[0], 10) === moment().subtract(1, 'hour').startOf('hour').unix());
                value = datum ? datum[1] : null;
            }
            // if we want sum for today but metric is not in available measures
            if (aggregationIdOrMetric === 'today' && !isMetricInAvailableMeasures) {
                value = forecasts.reduce((sum, datum) => {
                    if (isNil(sum))
                        return datum[1];
                    if (parseInt(datum[0], 10) < moment().startOf('hour').unix())
                        sum += datum[1] || 0;
                    return sum;
                }, null);
            }
            // if we want sum until midnight
            if (aggregationIdOrMetric === 'untilMidnight') {
                value = computeUntilMidnight(forecasts, timezone);
            }
            fullSummaryValue = {
                ...fullSummaryValue,
                fromForecasts,
                timestamp,
                hasDewRisk,
                value
            };
        }
        else if (isMetric(aggregationIdOrMetric)) {
            const { value, timestamp } = getAggregateValue(aggregationIdOrMetric, backEndAggregationsId || 'latest', summary);
            const fromForecasts = !isNil(timestamp)
                ? moment.unix(parseInt(timestamp, 10)).isAfter(moment())
                : false;
            fullSummaryValue = {
                ...fullSummaryValue,
                fromForecasts,
                timestamp,
                hasDewRisk,
                value
            };
        }
        return fullSummaryValue;
    }, [forecasts, metric, getAggregateValue, summary, isMetricInAvailableMeasures]);
    return {
        hasVirtualDevice: isVirtualDevice,
        metricProvider,
        getFullSummaryValue,
        warningThreshold,
        deviceId
    };
};
export default useMetricSummary;
